import PropTypes from 'prop-types'
import { kea } from 'kea'
import Bugsnag from '~/scenes/bugsnag'
import scrollToError from 'lib/utils/scroll-to-error'
import RawField from 'lib/kea/field'
import RawAdminField from 'lib/kea/admin-field'

const noopO = {}
const noop = () => noopO

export default function createListenerForm (optionsInput) {
  const options = {
    connect: undefined,
    key: undefined,
    path: undefined,
    defaults: {},
    actions: () => {},
    listeners: () => {}, // submitRequest
    reducers: () => {},
    selectors: () => {}, // validationErrors
    propTypes: {},

    noField: false,
    submitWhenNothingChanged: false,
    containerId: null, // limit errors to e.g. #general-form
    autoSave: noop,

    ...optionsInput
  }
  const propType = options.propTypes ? PropTypes.shape(options.propTypes) : PropTypes.object

  const response = kea({
    connect: options.connect,
    key: options.key,
    path: options.path,

    actions: logic => ({
      enableIsAutoSaving: true,
      disableIsAutoSaving: true,
      autoSave: true,
      setInitialState: initialState => ({ initialState }),
      setAutoSavedState: autoSavedState => ({ autoSavedState }),
      setValue: (key, value) => ({ key, value }),
      setValues: values => ({ values }),
      clearChanges: true,

      startManualValidation: (field = 'validating') => ({ field }),
      endManualValidation: true,

      setErrors: errors => ({ errors }),
      mergeErrors: errors => ({ errors }),
      clearErrors: true,
      showErrors: true,
      showErrorsForField: field => ({ field }),
      clearErrorsForField: field => ({ field }),

      submit: true,
      submitRequest: true,
      submitSuccess: (response, values) => ({ response, values }),
      submitFailure: error => ({ error }),

      cancelAndRedirect: redirectUrl => ({ redirectUrl }),
      toggleRedirecting: status => ({ status }),

      ...(options.actions ? options.actions(logic) : {})
    }),

    defaults: args => state => {
      const obj = typeof options.defaults === 'function' ? options.defaults(state) : options.defaults
      return {
        initialState: typeof obj === 'function' ? options.defaults(args) : obj
      }
    },

    reducers: logic => {
      const { actions } = logic
      return {
        initialState: [
          {},
          PropTypes.object,
          Object.assign({
            [actions.setInitialState]: (_, payload) => payload.initialState
          })
        ],

        isAutoSaving: [
          false,
          PropTypes.bool,
          {
            [actions.enableIsAutoSaving]: () => true,
            [actions.disableIsAutoSaving]: () => false
          }
        ],

        autoSavedState: [
          {},
          PropTypes.object,
          Object.assign({
            [actions.setInitialState]: (_, payload) => payload.initialState,
            [actions.setAutoSavedState]: (_, payload) => payload.autoSavedState
          })
        ],

        changes: [
          {},
          PropTypes.object,
          {
            [actions.setValue]: (state, payload) => ({ ...state, [payload.key]: payload.value }),
            [actions.setValues]: (state, payload) => ({ ...state, ...payload.values }),
            [actions.submitSuccess]: () => ({}),
            [actions.clearChanges]: () => ({})
          }
        ],

        isRedirecting: [
          false,
          PropTypes.bool,
          {
            [actions.toggleRedirecting]: (_, payload) => payload.status
          }
        ],

        isCanceling: [
          false,
          PropTypes.bool,
          {
            [actions.cancelAndRedirect]: () => true
          }
        ],

        isSubmitting: [
          false,
          PropTypes.bool,
          {
            [actions.submitRequest]: () => true,
            [actions.submitSuccess]: () => false,
            [actions.submitFailure]: () => false
          }
        ],

        isValidating: [
          null,
          PropTypes.string,
          {
            [actions.startManualValidation]: (_, payload) => payload.field,
            [actions.endManualValidation]: () => null
          }
        ],

        manualErrors: [
          {},
          PropTypes.object,
          {
            [actions.setErrors]: (_, payload) => payload.errors,
            [actions.clearErrors]: () => ({}),
            [actions.mergeErrors]: (state, payload) => ({ ...state, ...payload.errors }),
            [actions.setValue]: (state, payload) => {
              if (Object.keys(state).length > 0) {
                const { [payload.key]: discard, ...rest } = state
                return rest
              } else {
                return state
              }
            },
            [actions.submitSuccess]: () => ({}),
            [actions.clearChanges]: () => ({})
          }
        ],

        showErrors: [
          false,
          PropTypes.bool,
          {
            [actions.submit]: () => true,
            [actions.submitSuccess]: () => false,
            [actions.clearChanges]: () => false,
            [actions.showErrors]: () => true
          }
        ],

        showErrorsForFields: [
          [],
          PropTypes.array,
          {
            [actions.showErrorsForField]: (state, payload) =>
              state.filter(f => f !== payload.field).concat([payload.field]),
            [actions.clearErrorsForField]: (state, payload) => state.filter(f => f !== payload.field),
            [actions.submit]: () => [],
            [actions.submitSuccess]: () => [],
            [actions.clearChanges]: () => [],
            [actions.showErrors]: () => []
          }
        ],
        ...(options.reducers ? options.reducers(logic) : {})
      }
    },

    selectors: logic => {
      const { selectors } = logic
      return {
        formValues: [
          () => [selectors.initialState, selectors.changes],
          (initialState, changes) => Object.assign({}, initialState, changes),
          propType
        ],

        values: [
          // for legacy support
          () => [selectors.formValues],
          formValues => formValues
        ],

        hasChanges: [
          () => [selectors.initialState, selectors.changes],
          (initialState, changes) => {
            return Object.keys(changes).filter(k => changes[k] !== initialState[k]).length > 0
          },
          PropTypes.bool
        ],

        validationErrors: [
          () => [selectors.formValues],
          formValues => ({
            // keyWithError: 'Error message'
            // noError: undefined
          }),
          PropTypes.object
        ],

        hasErrors: [
          () => [selectors.validationErrors, selectors.manualErrors],
          (validationErrors, manualErrors) =>
            Object.values(validationErrors).filter(k => k).length > 0 ||
            Object.values(manualErrors).filter(k => k).length > 0,
          PropTypes.bool
        ],

        canSubmit: [
          () => [selectors.hasErrors, selectors.hasChanges, selectors.isValidating],
          (hasErrors, hasChanges, isValidating) =>
            !isValidating && !hasErrors && (options.submitWhenNothingChanged || hasChanges),
          PropTypes.bool
        ],

        hasAfterAutoSaveChanges: [
          () => [selectors.autoSavedState, selectors.changes],
          (autoSavedState, changes) => {
            return Object.keys(changes).filter(k => changes[k] !== autoSavedState[k]).length > 0
          },
          PropTypes.bool
        ],

        canAutoSave: [
          () => [
            selectors.hasChanges,
            selectors.isValidating,
            selectors.hasAfterAutoSaveChanges,
            selectors.isAutoSaving,
            selectors.isSubmitting
          ],
          (hasChanges, isValidating, hasAfterAutoSaveChanges, isAutoSaving, isSubmitting) =>
            !isValidating && hasChanges && hasAfterAutoSaveChanges && !isAutoSaving && !isSubmitting,
          PropTypes.bool
        ],

        currentErrors: [
          () => [selectors.validationErrors, selectors.manualErrors],
          (validationErrors, manualErrors) => ({ ...validationErrors, ...manualErrors }),
          PropTypes.object
        ],

        errors: [
          () => [
            selectors.validationErrors,
            selectors.manualErrors,
            selectors.showErrors,
            selectors.showErrorsForFields
          ],
          (validationErrors, manualErrors, showErrors, showErrorsForFields) => {
            if (showErrors || showErrorsForFields.length > 0) {
              const errors = { ...validationErrors, ...manualErrors }

              if (showErrors) {
                return errors
              } else {
                const fieldErrors = {}
                for (const field of showErrorsForFields) {
                  fieldErrors[field] = errors[field]
                }
                return fieldErrors
              }
            }
            return {}
          },
          PropTypes.object
        ],

        ...(options.selectors ? options.selectors(logic) : {})
      }
    },

    listeners: logic => {
      const { actions, values } = logic

      return {
        [actions.autoSave]: async function (_, breakpoint) {
          if (!values.canAutoSave) {
            return
          }
          try {
            actions.enableIsAutoSaving()

            const { formValues } = values
            const response = await options.autoSave(formValues)
            breakpoint()

            if (response.success) {
              actions.setAutoSavedState(formValues)
            }
            actions.disableIsAutoSaving()
          } catch (error) {
            Bugsnag.notify(error)
          }
        },

        [actions.submit]: async function () {
          if (values.canSubmit) {
            actions.submitRequest()
          } else {
            if (options.containerId) {
              const container =
                typeof options.containerId === 'function' ? options.containerId(this) : options.containerId
              scrollToError(container)
            }
          }
        },

        [actions.submitRequest]: async function () {
          try {
            const { formValues } = values

            throw 'You must override the "submitRequest" listener' // eslint-ignore-line
            // const response = await controller.getData()
            // messg.success(I18n.t('profiles.edit.changes_saved'), 2500)

            actions.setInitialState(formValues)
            actions.submitSuccess(response, formValues)
          } catch (error) {
            actions.submitFailure(error)
          }
        },

        [actions.cancelAndRedirect]: function ({ redirectUrl }) {
          window.location.href = redirectUrl
          window.setTimeout(() => {
            window.location.href = redirectUrl
          }, 15000)
        },

        ...(options.listeners ? options.listeners(logic) : {})
      }
    }
  })

  if (!options.noField) {
    response.getField = () => response(RawField)
    response.getAdminField = () => response(RawAdminField)
  }

  return response
}
